import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forget-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: { message?: string } })?.error?.message ??
            t("errors.generic")
        );
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at center, var(--bg-card) 0%, var(--bg-page) 70%)",
      }}
    >
      <div
        className="w-full max-w-md p-8 rounded-lg"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-default)",
        }}
      >
        <div className="text-center mb-8">
          <h1
            className="font-mono text-2xl font-medium tracking-[3px]"
            style={{ color: "var(--text-primary)" }}
          >
            KIBORG
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {t("auth.resetYourPassword")}
          </p>
        </div>

        {submitted ? (
          <div className="text-center space-y-4">
            <div
              className="p-4 rounded text-sm"
              style={{
                background: "var(--success-bg)",
                color: "var(--success)",
                border: "1px solid var(--success)",
              }}
            >
              {t("auth.resetLinkSent")}
            </div>
            <Link
              to="/login"
              className="block text-sm mt-4"
              style={{ color: "var(--accent)" }}
            >
              {t("auth.backToSignIn")}
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div
                className="mb-4 p-3 rounded text-sm"
                style={{
                  background: "var(--danger-bg)",
                  color: "var(--danger)",
                  border: "1px solid var(--danger)",
                }}
              >
                {error}
              </div>
            )}

            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
              {t("auth.enterEmail")}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {t("auth.email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@kiborg.io"
                  required
                  className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
                  style={{
                    background: "var(--bg-input)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border-default)",
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-md text-sm font-medium transition-colors"
                style={{
                  background: "var(--accent)",
                  color: "#fff",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? t("auth.sending") : t("auth.sendResetLink")}
              </button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm"
                style={{ color: "var(--accent)" }}
              >
                {t("auth.backToSignIn")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
